import { createContext, useContext, useEffect, useState } from 'react';
import { publicSchema } from './supabase';
import { supabase } from './supabase';
import { setStoredAccessToken } from './queryClient';
import type { User, Session } from '@supabase/supabase-js';

type UserRole = 'super_admin' | 'admin' | 'user' | null;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userRole: UserRole;
  loading: boolean;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ session: Session | null; user: User | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ROLE_CACHE_KEY = 'autolisting_user_role';

// Get cached role from localStorage
const getCachedRole = (userId: string): UserRole => {
  try {
    const cached = localStorage.getItem(ROLE_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      // Only use cache if it matches current user
      if (parsed.userId === userId && parsed.role) {
        return parsed.role as UserRole;
      }
    }
  } catch {
    // Ignore localStorage errors
  }
  return null;
};

// Cache role in localStorage
const setCachedRole = (userId: string, role: UserRole) => {
  try {
    if (role) {
      localStorage.setItem(ROLE_CACHE_KEY, JSON.stringify({ userId, role }));
    } else {
      localStorage.removeItem(ROLE_CACHE_KEY);
    }
  } catch {
    // Ignore localStorage errors
  }
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<UserRole>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserRole = async (session: Session | null): Promise<UserRole> => {
    if (!session?.access_token || !session?.user?.id) {
      return null;
    }

    // Get cached role first as fallback
    const cachedRole = getCachedRole(session.user.id);

    try {
      const response = await fetch('/api/auth/role', {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (!response.ok) {
        console.error('Role fetch failed:', response.status, response.statusText);
        // Return cached role on failure
        return cachedRole;
      }

      const data = await response.json();
      const role = data.role as UserRole;
      
      // Cache the successfully fetched role
      if (role) {
        setCachedRole(session.user.id, role);
      }
      
      return role;
    } catch (err) {
      // Better error logging
      console.error('Exception fetching user role:', err instanceof Error ? err.message : String(err), err);
      // During network failures, return cached role instead of null
      // This prevents random logouts when API is temporarily unavailable
      return cachedRole;
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      // Store token globally for API calls
      setStoredAccessToken(session?.access_token ?? null);
      
      if (session?.user) {
        const role = await fetchUserRole(session);
        setUserRole(role);
      } else {
        setUserRole(null);
      }
      
      setLoading(false);
    }).catch((err) => {
      console.error('AuthProvider: Error getting session:', err);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      // Store token globally for API calls
      setStoredAccessToken(session?.access_token ?? null);
      
      if (session?.user) {
        const role = await fetchUserRole(session);
        setUserRole(role);
      } else {
        setUserRole(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    });
    if (error) {
      console.error('AuthContext: signUp error', error);
      throw error;
    }
    return { session: data.session, user: data.user };
  };

  const signOut = async () => {
    localStorage.removeItem(ROLE_CACHE_KEY);
    localStorage.removeItem('currentOrganizationId');
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.error('Sign out error (forcing cleanup):', e);
    }
    setUser(null);
    setSession(null);
    setUserRole(null);
  };

  const isSuperAdmin = userRole === 'super_admin';
  const isAdmin = userRole === 'admin' || userRole === 'super_admin';

  return (
    <AuthContext.Provider value={{ user, session, userRole, loading, isSuperAdmin, isAdmin, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
